// Minimal POST-capable Server-Sent Events client. EventSource is GET-only, so
// we read the streaming response body ourselves and parse the `event:`/`data:`
// frames. Events are delimited by a blank line per the SSE spec.

export interface SseHandlers<T> {
  onProgress: (message: string) => void;
  onResult: (result: T) => void;
  onError?: (message: string) => void;
  /** Called for any other event type (e.g. per-row updates), with the data
   *  JSON-parsed when possible (raw string otherwise). */
  onEvent?: (event: string, data: unknown) => void;
}

export async function consumeSse<T>(
  response: Response,
  handlers: SseHandlers<T>,
): Promise<void> {
  if (!response.ok) {
    handlers.onError?.(`HTTP ${response.status}`);
    return;
  }
  if (!response.body) {
    handlers.onError?.("No response body");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let lastResult: T | null = null;
  let receivedError: string | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by \n\n (or \r\n\r\n). Split on either.
    let sepIdx = -1;
    while ((sepIdx = findFrameEnd(buffer)) !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx).replace(/^(\r?\n){2,}/, "");

      const parsed = parseFrame(frame);
      if (!parsed) continue;

      if (parsed.event === "progress") {
        try {
          handlers.onProgress(JSON.parse(parsed.data) as string);
        } catch {
          handlers.onProgress(parsed.data);
        }
      } else if (parsed.event === "result") {
        try {
          lastResult = JSON.parse(parsed.data) as T;
        } catch (err) {
          receivedError = `Bad result JSON: ${
            err instanceof Error ? err.message : String(err)
          }`;
        }
      } else if (parsed.event === "error") {
        try {
          receivedError = JSON.parse(parsed.data) as string;
        } catch {
          receivedError = parsed.data;
        }
      } else if (handlers.onEvent) {
        let payload: unknown = parsed.data;
        try {
          payload = JSON.parse(parsed.data);
        } catch {
          /* leave as raw string */
        }
        handlers.onEvent(parsed.event, payload);
      }
    }
  }

  if (receivedError !== null) {
    handlers.onError?.(receivedError);
  } else if (lastResult !== null) {
    handlers.onResult(lastResult);
  } else {
    handlers.onError?.("Stream ended without result");
  }
}

function findFrameEnd(buffer: string): number {
  const a = buffer.indexOf("\n\n");
  const b = buffer.indexOf("\r\n\r\n");
  if (a === -1) return b === -1 ? -1 : b;
  if (b === -1) return a;
  return Math.min(a, b);
}

function parseFrame(frame: string): { event: string; data: string } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const raw of frame.split(/\r?\n/)) {
    if (raw.startsWith("event:")) event = raw.slice(6).trim();
    else if (raw.startsWith("data:")) dataLines.push(raw.slice(5).trimStart());
    // ignore id:, retry:, and comments (lines starting with :)
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}
